import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import Layout from '../components/Layout';
import api from '../components/api';

// Lazy loading des composants dashboard pour améliorer les performances
const DashboardHeader = lazy(() => import('../components/dashboard/DashboardHeader'));
const DashboardStats = lazy(() => import('../components/dashboard/DashboardStats'));
const DashboardCharts = lazy(() => import('../components/dashboard/DashboardCharts'));
const DashboardDetailedStats = lazy(() => import('../components/dashboard/DashboardDetailedStats'));
const DashboardFooter = lazy(() => import('../components/dashboard/DashboardFooter'));
const ConfigurationRequired = lazy(() => import('../components/dashboard/ConfigurationRequired'));
const AnneeAcademiqueRequired = lazy(() => import('../components/dashboard/AnneeAcademiqueRequired'));
const NoDataAvailable = lazy(() => import('../components/dashboard/NoDataAvailable'));
const FinancialMetrics = lazy(() => import('../components/dashboard/FinancialMetrics'));
const PremiumLoader = lazy(() => import('../components/common/PremiumLoader'));
const ErrorState = lazy(() => import('../components/common/ErrorState'));

// Composant de fallback pour le lazy loading
const DashboardFallback = () => (
  <div className="flex items-center justify-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const DashboardPage = () => {
  const [pageState, setPageState] = useState('loading'); // 'loading', 'dashboard', 'config-required', 'annee-required', 'no-data', 'error'
  const [dashboardData, setDashboardData] = useState(null);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [error, setError] = useState(null);

  // Mémoriser la fonction de changement d'année pour éviter les re-renders
  const handleYearChange = useCallback(async (newYear) => {
    if (newYear === selectedAnnee) return; // Éviter les appels inutiles
    
    setPageState('loading');
    try {
      const response = await api.get(`/etablissement/dashboard-stats/?annee_academique=${newYear}`);
      setDashboardData(response.data);
      setSelectedAnnee(newYear);
      
      const hasAnyData = response.data.etudiants?.total_inscriptions > 0 ||
                        response.data.classes?.total_classes > 0 ||
                        response.data.enseignants?.total_enseignants > 0 ||
                        response.data.examens?.total_examens_planifies > 0;
      
      setPageState(hasAnyData ? 'dashboard' : 'no-data');
    } catch (error) {
      setError('Erreur lors du chargement des statistiques');
      setPageState('error');
    }
  }, [selectedAnnee]);

  // Mémoriser la fonction de formatage des données pour éviter les recalculs
  const formatChartData = useCallback((data, labelKey, valueKey = 'count') => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map(item => {
      let name = item[labelKey] || 'Non défini';
      
      // Formater les genres
      if (labelKey === 'etudiant__genre') {
        name = name === 'M' ? 'Masculin' : 
               name === 'F' ? 'Féminin' : 
               name === 'MASCULIN' ? 'Masculin' :
               name === 'FEMININ' ? 'Féminin' :
               name;
      }
      
      return {
        name: name,
        [valueKey]: item[valueKey] || item.count || 0,
        pourcentage: item.pourcentage || 0
      };
    });
  }, []);

  // Mémoriser les données formatées pour éviter les recalculs
  const memoizedDashboardData = useMemo(() => dashboardData, [dashboardData]);
  const memoizedAnneesAcademiques = useMemo(() => anneesAcademiques, [anneesAcademiques]);

  // Une seule initialisation complète
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // 1. Vérifier l'établissement
        let etablissementResponse;
        try {
          etablissementResponse = await api.get('/etablissement/etablissement-infos/');
        } catch (error) {
          if (error.response?.status === 404) {
            setPageState('config-required');
            return;
          } else {
            throw new Error('Erreur lors de la vérification de l\'établissement');
          }
        }

        // 2. Charger les années académiques
        let anneesResponse;
        try {
          anneesResponse = await api.get('/etablissement/annees-academiques/');
          if (!anneesResponse.data || anneesResponse.data.length === 0) {
            setPageState('annee-required');
            return;
          }
        } catch (error) {
          if (error.response?.status === 404) {
            setPageState('annee-required');
            return;
          } else {
            throw new Error('Erreur lors du chargement des années académiques');
          }
        }

        // 3. Sélectionner l'année active
        const anneeActive = anneesResponse.data.find(annee => annee.status);
        const selectedYear = anneeActive 
          ? anneeActive.id.toString() 
          : anneesResponse.data[0]?.id.toString();

        if (!selectedYear) {
          setPageState('annee-required');
          return;
        }

        // 4. Charger les statistiques
        const statsResponse = await api.get(`/etablissement/dashboard-stats/?annee_academique=${selectedYear}`);
        
        // Vérifier s'il y a des données
        const hasAnyData = statsResponse.data.etudiants?.total_inscriptions > 0 ||
                          statsResponse.data.classes?.total_classes > 0 ||
                          statsResponse.data.enseignants?.total_enseignants > 0 ||
                          statsResponse.data.examens?.total_examens_planifies > 0;

        // 5. Tout mettre à jour d'un coup à la fin
        setAnneesAcademiques(anneesResponse.data);
        setSelectedAnnee(selectedYear);
        setDashboardData(statsResponse.data);
        
        if (hasAnyData) {
          setPageState('dashboard');
        } else {
          setPageState('no-data');
        }

      } catch (error) {
        console.error('Erreur initialisation:', error);
        setError(error.message || 'Erreur lors du chargement de la page');
        setPageState('error');
      }
    };

    initializeApp();
  }, []);

  // Mémoriser les composants pour éviter les re-renders inutiles
  const dashboardContent = useMemo(() => {
    if (pageState !== 'dashboard') return null;
    
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30">
          <div className="p-6 space-y-8">
            <Suspense fallback={<DashboardFallback />}>
              {/* En-tête */}
              <DashboardHeader
                anneesAcademiques={memoizedAnneesAcademiques}
                selectedAnnee={selectedAnnee}
                setSelectedAnnee={handleYearChange}
                anneeAcademique={memoizedDashboardData?.general?.annee_academique}
              />

              {/* Cartes de statistiques principales */}
              <DashboardStats stats={memoizedDashboardData} />

              {/* Métriques financières */}
              <FinancialMetrics
                financeStats={memoizedDashboardData?.finance}
                scolariteStats={memoizedDashboardData?.scolarite}
                versementsStats={memoizedDashboardData?.versements}
              />

              {/* Graphiques */}
              <DashboardCharts stats={memoizedDashboardData} formatChartData={formatChartData} />

              {/* Statistiques détaillées */}
              <DashboardDetailedStats stats={memoizedDashboardData} />

              {/* Footer avec dernière mise à jour */}
              <DashboardFooter stats={memoizedDashboardData} />
            </Suspense>
          </div>
        </div>
      </Layout>
    );
  }, [pageState, memoizedDashboardData, memoizedAnneesAcademiques, selectedAnnee, handleYearChange, formatChartData]);

  // Rendu basé sur un seul état
  switch (pageState) {
    case 'loading':
      return <PremiumLoader type="dashboard" />;

    case 'error':
      return (
        <ErrorState
          title="Erreur de chargement"
          message={error}
          onRetry={() => window.location.reload()}
          type="error"
        />
      );

    case 'config-required':
      return (
        <Layout>
          <ConfigurationRequired />
        </Layout>
      );

    case 'annee-required':
      return (
        <Layout>
          <AnneeAcademiqueRequired />
        </Layout>
      );

    case 'no-data':
      return (
        <Layout>
          <NoDataAvailable
            anneesAcademiques={memoizedAnneesAcademiques}
            selectedAnnee={selectedAnnee}
            setSelectedAnnee={handleYearChange}
            anneeAcademique={memoizedDashboardData?.general?.annee_academique}
          />
        </Layout>
      );

    case 'dashboard':
    default:
      return dashboardContent;
  }
};

export default DashboardPage;
